// React
import { useState, useEffect, useCallback, useMemo } from "react";

// Third-party
import {
  User,
  Search,
  Heart,
  Bell,
  Settings,
  Code,
  Copy,
  Check,
  Eye,
  EyeOff,
  ExternalLink,
} from "lucide-react";

// Hooks & Contexts
import { useTranslation, useSnackbar } from "@/hooks";
import { useAuth } from "@/contexts";

// Components
import { Header } from "@/components/Header";

// Config
import { env } from "@/config/env";

// Constants
import { StorageKeys } from "@/constants";

/**
 * Role badge color mapping
 * @param role - User role string
 * @returns Tailwind classes for the role badge
 */
function getRoleBadgeClasses(role: string): string {
  switch (role) {
    case "admin":
      return "bg-green-100 text-green-800";
    case "developer":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/**
 * Check if user has developer-level access
 * @param globalRoles - Array of user's global roles
 * @returns True if user has admin or developer role
 */
function hasDevAccess(globalRoles: string[] | undefined): boolean {
  return (
    globalRoles?.some((role) => role === "admin" || role === "developer") ??
    false
  );
}

/**
 * Get display name from user data
 * @param user - Firebase user object
 * @returns Human-readable display name
 */
function getDisplayName(
  user: { displayName?: string | null; email?: string | null } | null,
): string {
  if (!user) return "User";
  return user.displayName || user.email?.split("@")[0] || "User";
}

export function DashboardPage() {
  const t = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { user, getIdToken, authMe } = useAuth();

  // State
  const [token, setToken] = useState<string>("");
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  // Derived state
  const displayName = useMemo(() => getDisplayName(user), [user]);
  const showDevTools = useMemo(
    () => hasDevAccess(authMe?.globalRoles),
    [authMe?.globalRoles],
  );

  /**
   * Show welcome message only once per session (uses sessionStorage)
   */
  useEffect(() => {
    if (!user || !authMe) return;

    // Check if we've already shown welcome this session
    const welcomeKey = StorageKeys.dashboardWelcomeShown;
    try {
      if (sessionStorage.getItem(welcomeKey) === "1") return;
      sessionStorage.setItem(welcomeKey, "1");
    } catch {
      // Storage not available, skip showing to avoid duplicates
      return;
    }

    // Show appropriate welcome message based on role
    const isAdmin = authMe.globalRoles.includes("admin");
    if (isAdmin) {
      showSnackbar(t("welcomeAdminSnackbar"), "success", 5000);
    } else {
      showSnackbar(t("welcomeUserSnackbar"), "info", 5000);
    }
  }, [user, authMe, showSnackbar, t]);

  /**
   * Fetch ID token for developer tools
   */
  useEffect(() => {
    const fetchToken = async () => {
      const idToken = await getIdToken();
      if (idToken) {
        setToken(idToken);
      }
    };
    fetchToken();
  }, [getIdToken]);

  /**
   * Copy token to clipboard
   */
  const handleCopyToken = useCallback(async () => {
    if (token) {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [token]);

  /**
   * Toggle token visibility
   */
  const handleToggleToken = useCallback(() => {
    setShowToken((prev) => !prev);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header position="sticky" variant="scroll-aware" showShadow showBorder />

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t("dashboardWelcome")}, {displayName}!
          </h1>
          <p className="text-gray-600">{t("dashboardSubtitle")}</p>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-red-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {displayName}
              </h2>
              <p className="text-gray-500">{user?.email}</p>
              {authMe && authMe.globalRoles.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {authMe.globalRoles.map((role: string) => (
                    <span
                      key={role}
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeClasses(role)}`}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Search */}
          <div className="bg-white rounded-xl shadow border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-3">
              <Search className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {t("dashboardSearch")}
            </h3>
            <p className="text-sm text-gray-500">{t("dashboardSearchDesc")}</p>
          </div>

          {/* Favorites */}
          <div className="bg-white rounded-xl shadow border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-3">
              <Heart className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {t("dashboardFavorites")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("dashboardFavoritesDesc")}
            </p>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-xl shadow border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
              <Bell className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {t("dashboardNotifications")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("dashboardNotificationsDesc")}
            </p>
          </div>

          {/* Settings */}
          <div className="bg-white rounded-xl shadow border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
              <Settings className="w-6 h-6 text-gray-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {t("dashboardSettings")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("dashboardSettingsDesc")}
            </p>
          </div>
        </div>

        {/* Coming Soon Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-amber-800 text-sm text-center">
            <span className="font-semibold">🚧 {t("dashboardComingSoon")}</span>
            <br />
            {t("dashboardComingSoonDesc")}
          </p>
        </div>

        {/* Developer Tools - Only for admin/developer roles */}
        {showDevTools && (
          <div className="bg-slate-900 rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                <Code className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {t("dashboardDevTools")}
                </h3>
                <p className="text-sm text-slate-400">
                  {t("dashboardDevToolsDesc")}
                </p>
              </div>
            </div>

            <div className="bg-slate-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-300 mb-3">
                {t("dashboardTokenInfo")}{" "}
                <a
                  href={env.apiDocsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                >
                  Swagger UI
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>

              <div className="flex gap-2">
                <button
                  onClick={handleToggleToken}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium transition-colors"
                >
                  {showToken ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      {t("hide")}
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      {t("dashboardShowToken")}
                    </>
                  )}
                </button>
                <button
                  onClick={handleCopyToken}
                  disabled={!token}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t("dashboardCopied")}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      {t("dashboardCopyToken")}
                    </>
                  )}
                </button>
              </div>
            </div>

            {showToken && token && (
              <div className="bg-slate-800 rounded-lg p-4 overflow-hidden">
                <p className="text-xs font-mono break-all text-slate-300">
                  {token}
                </p>
              </div>
            )}

            {showToken && !token && (
              <p className="text-sm text-amber-400">
                {t("dashboardTokenError")}
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
